import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { PeopleAltOutlined } from '@mui/icons-material';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useToast } from '../contexts/ToastContext';

/** The filename from a `Content-Disposition: attachment; filename="…"` header, or null. */
export const attachmentFileName = (header: string | null): string | null => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header || '');
  return match ? decodeURIComponent(match[1].trim()) : null;
};

interface ProductsCsvExportButtonProps {
  /** API path of the export, e.g. `/store/sku-crosswalk/products.csv`. */
  path: string;
  /** Name the browser saves the file under. */
  fileName: string;
  size?: 'small' | 'medium';
}

/**
 * "Download my products": the surface's upload template filled in from the store's own products.
 * The server says when it cut the file (`X-Export-Capped`), which is passed on to the person.
 */
export const ProductsCsvExportButton: React.FC<ProductsCsvExportButtonProps> = ({ path, fileName, size = 'small' }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const response = await makeApiCall(path, { headers: await getAuthHeaders() });
      if (!response.ok) {
        const body = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(body?.error || t('csvTemplate.productsFailed'));
      }
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      // A cut file is named `…-first-N.csv` by the server; keep that name so the cut is visible on disk.
      link.download = attachmentFileName(response.headers.get('Content-Disposition')) || fileName;
      link.click();
      URL.revokeObjectURL(url);
      const omitted = Number(response.headers.get('X-Export-Omitted') || 0);
      const capped = response.headers.get('X-Export-Capped') === 'true';
      // The cap and the omitted count are independent signals and can both be true; report each.
      if (response.headers.get('X-Export-Rows') === '0') {
        showToast(t('csvTemplate.productsNone'), 'info');
      } else {
        if (capped) {
          showToast(t('csvTemplate.productsCapped', { limit: response.headers.get('X-Export-Rows') || '' }), 'info');
        }
        if (omitted > 0) {
          showToast(t('csvTemplate.productsOmitted', { count: omitted }), 'info');
        }
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('csvTemplate.productsFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size={size}
      variant="outlined"
      startIcon={busy ? <CircularProgress size={14} /> : <PeopleAltOutlined />}
      onClick={download}
      disabled={busy}
    >
      {busy ? t('csvTemplate.downloadingProducts') : t('csvTemplate.downloadProducts')}
    </Button>
  );
};

export default ProductsCsvExportButton;
